import { useStore } from "../Store";
import { useWipeData } from "./useWipeData";
import { Flow } from "../Types/SessionDetails";
import { createOAuthRedirect } from "./createOAuthRedirect";
import {redirectParentPage} from "./redirectParentPage";

export function useMessageParentOrRedirect() {
  const { setRedirecting } = useStore();
  const wipeData = useWipeData();

  function messageToParentUnauthorised({
    isEmbedded,
    redirectUri,
    consentId,
    state,
  }: UnauthorisedEventProps) {
    const event = {
      event: "ADRIS_CONSENT_FAILED",
      consentId,
      message: "Sorry, you are not authorized to access this page.",
      resultCode: 3000,
      state,
    };
    setRedirecting(true);
    messageParentOrRedirectBase(isEmbedded, event, redirectUri);
    wipeData();
  }

  function messageParentOrRedirect(
    event: "SUCCESS" | "CANCELLED" | "ERROR",
    data: {
      flow: "direct-to-bank" | "consent" | "manage" | "amend";
      consentId: string;
      cdrAgreementId?: string;
      state: string;
      message: string;
      isEmbedded: boolean;
      redirectUriBase: string;
      sessionId?: string;
    }
  ) {
    if (data.flow === "direct-to-bank" || data.flow === "amend") {
      setRedirecting(true);
      messageParentOrRedirectForDirectToBankOrAmendFlow(event, data.flow, {
        state: data.state,
        message: data.message,
        isEmbedded: data.isEmbedded,
        redirectUriBase: data.redirectUriBase,
        sessionId: data.sessionId,
      });
      wipeData();
    } else if (data.flow === "consent") {
      setRedirecting(true);
      messageParentOrRedirectForConsentFlow(event, { ...data });
      wipeData();
    } else if (data.flow === "manage") {
      throw new Error("Cannot redirect the manage flow");
    }
  }

  return {
    messageToParentUnauthorised,
    messageParentOrRedirect,
  };
}

function messageParentOrRedirectForDirectToBankOrAmendFlow(
  event: "SUCCESS" | "CANCELLED" | "ERROR",
  flow: "direct-to-bank" | "amend",
  data: {
    state: string;
    message: string;
    isEmbedded: boolean;
    redirectUriBase: string;
    sessionId?: string;
  }
) {
  const eventCode = getResultCode(event);

  messageParentOrRedirectBase(
    data.isEmbedded,
    {
      event: event,
      message: data.message,
      resultCode: eventCode,
      result: event,
      state: data.state,
      sessionId: data.sessionId,
    },
    getRedirectUri({
      flow: flow,
      redirectUriBase: data.redirectUriBase,
      state: data.state,
      message: data.message,
      resultCode: eventCode,
      result: event,
      sessionId: data.sessionId,
    })
  );
}

function messageParentOrRedirectForConsentFlow(
  event: "SUCCESS" | "CANCELLED" | "ERROR",
  data: {
    consentId: string;
    cdrAgreementId?: string;
    state: string;
    message: string;
    isEmbedded: boolean;
    redirectUriBase: string;
    sessionId?: string;
  }
) {
  const eventCode = getResultCode(event);

  messageParentOrRedirectBase(
    data.isEmbedded,
    {
      event: event,
      consentId: data.consentId,
      message: data.message,
      resultCode: eventCode,
      result: event,
      state: data.state,
      sessionId: data.sessionId,
    },
    getRedirectUri({
      flow: "consent",
      redirectUriBase: data.redirectUriBase,
      cdrAgreementId: data.cdrAgreementId,
      consentId: data.consentId,
      state: data.state,
      message: data.message,
      resultCode: eventCode,
      result: event,
      sessionId: data.sessionId,
    })
  );
}

const messageParentOrRedirectBase = (
  isEmbedded: boolean,
  event: any,
  redirectUri: string
) => {
  if (isEmbedded) {
    // eslint-disable-next-line no-restricted-globals
    parent.postMessage(event, "*");
  } else {
    redirectParentPage(redirectUri);
  }
};

interface UnauthorisedEventProps {
  isEmbedded: boolean;
  redirectUri: string;
  consentId: string;
  state: string;
}

function getRedirectUri(params: {
  flow: Flow;
  result: "SUCCESS" | "CANCELLED" | "ERROR";
  resultCode: 1000 | 2000 | 3000;
  redirectUriBase: string;
  consentId?: string;
  cdrAgreementId?: string;
  state: string;
  message: string;
  sessionId?: string;
}) {
  const {
    flow,
    result,
    resultCode,
    redirectUriBase,
    consentId, // Note that consentId MUST NOT be returned during direct-to-bank
    cdrAgreementId, // Note that cdrAgreementId MUST NOT be returned during direct-to-bank
    state,
    message,
    sessionId,
  } = params;

  if (flow === "direct-to-bank")
    return createOAuthRedirect({ ...params, flow: "direct-to-bank" });
  
  if(flow === "amend")
    return createOAuthRedirect({ ...params, flow: "amend" });

  const resultText = result.toLowerCase();

  let redirectUriHasQueryString = redirectUriBase.includes("?");
  let redirectUri = `${redirectUriBase}${
    redirectUriHasQueryString ? "&" : "?"
  }result=${resultText}&result-code=${resultCode}&state=${state}&message=${message}`;

  if (consentId) {
    redirectUri += `&consent-id=${consentId}`;
  }

  if (cdrAgreementId) {
    redirectUri += `&cdr-agreement-id=${cdrAgreementId}`;
  }

  if (sessionId) {
    redirectUri += `&session-id=${sessionId}`;
  }

  return redirectUri;
}

function getResultCode(event: "SUCCESS" | "CANCELLED" | "ERROR") {
  if (event === "SUCCESS") {
    return 1000;
  }
  if (event === "CANCELLED") {
    return 2000;
  }
  if (event === "ERROR") {
    return 3000;
  }

  throw new Error("Event has no result code");
}
